import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePlatformAuthStore } from '@/stores/platformAuthStore';
import {
  createPlan,
  createPlatformAdmin,
  createTenant,
  deleteTenant,
  deletePlan,
  deletePlatformAdmin,
  fetchImpersonations,
  fetchPlans,
  fetchPlatformAdmins,
  fetchDeletionPreview,
  fetchPlatformDashboard,
  fetchPlatformSettings,
  fetchSubscriptionStatuses,
  fetchSubscriptions,
  fetchSupportMeta,
  fetchSupportStats,
  fetchSupportThread,
  fetchSupportTickets,
  fetchTenant,
  fetchTenantStatuses,
  fetchTenants,
  fetchWebhookEvent,
  fetchWebhookEventTypes,
  fetchWebhookEvents,
  impersonateTenant,
  platformLogin,
  platformLogout,
  replayWebhookEvent,
  replyToSupportTicket,
  restoreTenant,
  suspendTenant,
  testEmail,
  testRazorpay,
  updatePlan,
  updatePlatformAdmin,
  updatePlatformSettings,
  updateSubscription,
  updateSupportTicket,
  updateTenant,
} from './api';

export const platformKeys = {
  all: ['platform'] as const,
  dashboard: () => [...platformKeys.all, 'dashboard'] as const,
  tenants: (filters: Record<string, unknown>) => [...platformKeys.all, 'tenants', filters] as const,
  tenant: (uuid: string) => [...platformKeys.all, 'tenant', uuid] as const,
  deletionPreview: (uuid: string) => [...platformKeys.all, 'deletion-preview', uuid] as const,
  plans: () => [...platformKeys.all, 'plans'] as const,
  subscriptions: (filters: Record<string, unknown>) =>
    [...platformKeys.all, 'subscriptions', filters] as const,
  webhooks: (filters: Record<string, unknown>) =>
    [...platformKeys.all, 'webhooks', filters] as const,
  webhook: (id: number) => [...platformKeys.all, 'webhook', id] as const,
  admins: () => [...platformKeys.all, 'admins'] as const,
  impersonations: () => [...platformKeys.all, 'impersonations'] as const,
  settings: () => [...platformKeys.all, 'settings'] as const,
  support: (filters: Record<string, unknown>) =>
    [...platformKeys.all, 'support', filters] as const,
  supportThread: (reference: string) =>
    [...platformKeys.all, 'support-thread', reference] as const,
  supportStats: () => [...platformKeys.all, 'support-stats'] as const,
  lookups: (kind: string) => [...platformKeys.all, 'lookup', kind] as const,
};

/* ---- Session ------------------------------------------------------------ */

export function usePlatformLogin() {
  const setToken = usePlatformAuthStore((s) => s.setToken);
  const setAdmin = usePlatformAuthStore((s) => s.setAdmin);

  return useMutation({
    mutationFn: platformLogin,
    onSuccess: ({ token, admin }) => {
      setToken(token);
      setAdmin(admin);
    },
  });
}

export function usePlatformLogout() {
  const clear = usePlatformAuthStore((s) => s.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: platformLogout,
    // Cleared on settle, not success: a token the server has already dropped
    // returns 401 here, and refusing to sign out locally because of that
    // leaves someone stuck on a dead session.
    onSettled: () => {
      clear();
      queryClient.removeQueries({ queryKey: platformKeys.all });
    },
  });
}

/* ---- Dashboard ---------------------------------------------------------- */

export function usePlatformDashboard() {
  return useQuery({
    queryKey: platformKeys.dashboard(),
    queryFn: fetchPlatformDashboard,
    staleTime: 60_000,
  });
}

/* ---- Workspaces --------------------------------------------------------- */

export function useTenants(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: platformKeys.tenants(filters),
    queryFn: () => fetchTenants(filters),
    placeholderData: (previous) => previous,
  });
}

export function useTenant(uuid: string | undefined) {
  return useQuery({
    queryKey: platformKeys.tenant(uuid ?? ''),
    queryFn: () => fetchTenant(uuid as string),
    enabled: uuid !== undefined,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createTenant(payload),
    onSuccess: (tenant) => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      toast.success(`${tenant.name} is live`);
    },
  });
}

export function useUpdateTenant(uuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateTenant(uuid, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      toast.success('Workspace updated');
    },
  });
}

export function useSuspendTenant(uuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reason: string) => suspendTenant(uuid, reason),
    onSuccess: (tenant) => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      // The token count is the part an operator wants confirmed: suspension
      // that does not end live sessions is a suspension in name only.
      toast.success(`Suspended — ${tenant.tokens_revoked} session(s) ended`);
    },
  });
}

export function useRestoreTenant(uuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => restoreTenant(uuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      toast.success('Workspace restored');
    },
  });
}

/** Read-only, and only fetched while the dialog is open. */
export function useDeletionPreview(uuid: string | null) {
  return useQuery({
    queryKey: platformKeys.deletionPreview(uuid ?? ''),
    queryFn: () => fetchDeletionPreview(uuid as string),
    enabled: uuid !== null,
    // Always fresh: these counts are the basis of an irreversible decision,
    // and a cached "3 sales" against a shop that has since taken three hundred
    // is the one number that must not be stale.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDeleteTenant(uuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { confirm: string; reason?: string }) => deleteTenant(uuid, payload),
    onSuccess: (result) => {
      // removeQueries, not invalidate: refetching a tenant that no longer
      // exists would 404 into the error toast on the way out.
      queryClient.removeQueries({ queryKey: platformKeys.tenant(uuid) });
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });

      toast.success(`${result.name} deleted`, {
        description: `${result.rows_deleted.toLocaleString()} rows and ${result.files_deleted} file(s) removed.`,
      });
    },
  });
}

export function useImpersonate(uuid: string) {
  return useMutation({
    mutationFn: (allowWrites: boolean) => impersonateTenant(uuid, allowWrites),
  });
}

export function useTenantStatuses() {
  return useQuery({
    queryKey: platformKeys.lookups('tenant-statuses'),
    queryFn: fetchTenantStatuses,
    staleTime: Infinity,
  });
}

/* ---- Pricing ------------------------------------------------------------ */

export function usePlans() {
  return useQuery({ queryKey: platformKeys.plans(), queryFn: fetchPlans });
}

export function useSavePlan(slug: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      slug === null ? createPlan(payload) : updatePlan(slug, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.plans() });
      toast.success(slug === null ? 'Plan created' : 'Plan updated');
    },
  });
}

export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePlan,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.plans() });
      toast.success('Plan deleted');
    },
  });
}

/* ---- Subscriptions ------------------------------------------------------ */

export function useSubscriptions(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: platformKeys.subscriptions(filters),
    queryFn: () => fetchSubscriptions(filters),
    placeholderData: (previous) => previous,
  });
}

export function useUpdateSubscription(id: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateSubscription(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      toast.success('Subscription updated');
    },
  });
}

export function useSubscriptionStatuses() {
  return useQuery({
    queryKey: platformKeys.lookups('subscription-statuses'),
    queryFn: fetchSubscriptionStatuses,
    staleTime: Infinity,
  });
}

/* ---- Webhooks ----------------------------------------------------------- */

export function useWebhookEvents(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: platformKeys.webhooks(filters),
    queryFn: () => fetchWebhookEvents(filters),
    placeholderData: (previous) => previous,
  });
}

export function useWebhookEvent(id: number | null) {
  return useQuery({
    queryKey: platformKeys.webhook(id ?? 0),
    queryFn: () => fetchWebhookEvent(id as number),
    enabled: id !== null,
  });
}

export function useReplayWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: replayWebhookEvent,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.all });
      // Queued, not done. The job runs on the worker, so saying "replayed"
      // would claim an outcome nobody has yet.
      toast.success('Queued for replay — refresh in a moment');
    },
  });
}

export function useWebhookEventTypes() {
  return useQuery({
    queryKey: platformKeys.lookups('webhook-types'),
    queryFn: fetchWebhookEventTypes,
    staleTime: 5 * 60_000,
  });
}

/* ---- Us ----------------------------------------------------------------- */

export function usePlatformAdmins() {
  return useQuery({ queryKey: platformKeys.admins(), queryFn: fetchPlatformAdmins });
}

export function useSavePlatformAdmin(id: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      id === null ? createPlatformAdmin(payload) : updatePlatformAdmin(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.admins() });
      toast.success(id === null ? 'Administrator added' : 'Administrator updated');
    },
  });
}

export function useDeletePlatformAdmin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePlatformAdmin,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.admins() });
      toast.success('Administrator removed');
    },
  });
}

export function useImpersonations() {
  return useQuery({
    queryKey: platformKeys.impersonations(),
    queryFn: fetchImpersonations,
    // Short: this answers "who is in a customer's account right now", and a
    // cached answer to that is worse than no answer.
    staleTime: 10_000,
  });
}

/* ---- Operator settings --------------------------------------------------- */

export function usePlatformSettings() {
  return useQuery({
    queryKey: platformKeys.settings(),
    queryFn: fetchPlatformSettings,
  });
}

export function useSavePlatformSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updatePlatformSettings(payload),
    onSuccess: (settings) => {
      // setQueryData rather than invalidate: the response IS the fresh state,
      // and a refetch would briefly show the pre-save masks.
      queryClient.setQueryData(platformKeys.settings(), settings);
      toast.success('Settings saved');
    },
  });
}

export function useTestRazorpay() {
  return useMutation({
    mutationFn: testRazorpay,
    onSuccess: (result) => {
      // The failure message is the useful half — it names which credential
      // Razorpay rejected — so it is a toast, not a silent false.
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    },
  });
}

/**
 * Send one real email with whatever is stored.
 *
 * The message is the whole point on failure. "Connection could not be
 * established with host smtp.example.com:465" names the mistake; a generic
 * "could not send" leaves the operator exactly where they started, which is
 * why the endpoint returns the provider's own words and this shows them.
 */
export function useTestEmail() {
  return useMutation({
    mutationFn: (to: string) => testEmail(to),
    onSuccess: (result) => {
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    },
  });
}

/* ---- Support ------------------------------------------------------------- */

export function useSupportTickets(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: platformKeys.support(filters),
    queryFn: () => fetchSupportTickets(filters),
    placeholderData: (previous) => previous,
  });
}

export function useSupportThread(reference: string | undefined) {
  return useQuery({
    queryKey: platformKeys.supportThread(reference ?? ''),
    queryFn: () => fetchSupportThread(reference as string),
    enabled: reference !== undefined,
  });
}

/**
 * The queue counters.
 *
 * Polled: an operator leaves this console open all day, and a queue that only
 * updates on navigation is a queue that says zero while a shop waits.
 */
export function usePlatformSupportStats() {
  return useQuery({
    queryKey: platformKeys.supportStats(),
    queryFn: fetchSupportStats,
    refetchInterval: 60_000,
  });
}

export function useSupportMeta() {
  return useQuery({
    queryKey: platformKeys.lookups('support-meta'),
    queryFn: fetchSupportMeta,
    staleTime: 5 * 60_000,
  });
}

export function useReplySupportTicket(reference: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { body: string; is_internal?: boolean; resolve?: boolean }) =>
      replyToSupportTicket(reference, payload),
    onSuccess: (thread, variables) => {
      // The response IS the fresh thread — a refetch would blank the
      // conversation for a moment after every reply.
      queryClient.setQueryData(platformKeys.supportThread(reference), thread);
      void queryClient.invalidateQueries({ queryKey: platformKeys.supportStats() });
      toast.success(variables.is_internal === true ? 'Note added' : 'Reply sent');
    },
  });
}

export function useUpdateSupportTicket(reference: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateSupportTicket(reference, payload),
    onSuccess: (thread) => {
      queryClient.setQueryData(platformKeys.supportThread(reference), thread);
      void queryClient.invalidateQueries({ queryKey: platformKeys.supportStats() });
      toast.success('Ticket updated');
    },
  });
}
